import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Stack,
  Grid,
  Typography,
  Tooltip,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogActions,
  DialogContent,
  Card,
  CardHeader,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import SuccessMessage from '../../../components/successMessage';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ReceiptModal from '../../../financial/incomes/receiptModal';
import Loading from '../../../components/loading';
import ErrorMessage from '../../../components/errorMessage';
import IncomesModal from '../../../financial/incomes/incomesModal';
import UserAuthentication from '../../../components/userAuthentication';
import { usePatientIncomes } from '../../../financial/hooks/usePatientIncomes';
import { useIncomeActions } from '../../../financial/hooks/useIncomeActions';

export default function FinancialListWeb() {
  const { patientId } = useParams();

  const [selectedDate, setSelectedDate] = useState(dayjs());

  const {
    incomes,
    totalAmount,
    totalAmountToBeReceived,
    totalAmountReceived,
    isLoading: isListLoading,
    error: listError,
    refetch: getIncomes,
  } = usePatientIncomes(patientId, selectedDate);
  const { remove, isLoading: isActionLoading } = useIncomeActions();

  const isLoading = isListLoading || isActionLoading;

  const [openIncomeModal, setOpenIncomeModal] = useState(false);
  const [openReceiptModal, setOpenReceiptModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuSelectedIncome, setMenuSelectedIncome] = useState(null);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [selectedIncomeId, setSelectedIncomeId] = useState(null);

  const statusConfig = {
    'A Receber': {
      color: 'warning',
      variant: 'outlined',
    },
    Recebida: {
      color: 'success',
      variant: 'filled',
    },
    Atrasada: {
      color: 'error',
      variant: 'filled',
    },
  };

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const saveSuccess = (returnFromApi) => {
    setShowSuccessMessage(true);
    setSuccessMessage(returnFromApi.data);
    setOpenIncomeModal(false);
    setOpenReceiptModal(false);
    getIncomes();
  };

  const saveError = (returnFromApi) => {
    setShowErrorMessage(true);
    setErrorMessage(returnFromApi);
  };

  const openMenu = (event, income) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuSelectedIncome(income);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuSelectedIncome(null);
  };

  const editIncome = () => {
    closeMenu();
    setSelectedIncomeId(menuSelectedIncome.id);
    setOpenIncomeModal(true);
  };

  const addIncome = () => {
    closeMenu();
    setSelectedIncomeId(null);
    setOpenIncomeModal(true);
  };

  const receive = () => {
    closeMenu();
    setSelectedIncomeId(menuSelectedIncome.id);
    setOpenReceiptModal(true);
  };

  const clickDelete = () => {
    setOpenDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  const confirmDelete = () => {
    if (!menuSelectedIncome) return;
    const incomeId = menuSelectedIncome.id;

    closeDeleteModal();
    remove(incomeId)
      .then((returnFromApi) => {
        setShowSuccessMessage(true);
        setSuccessMessage(returnFromApi.data);
        getIncomes();
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data);
      });
  };

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}
      <Box sx={{ display: 'flex', alignItems: 'start' }}>
        <Box sx={{ flex: '1.5', minWidth: 0 }}>
          <UserAuthentication />
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardHeader
              title={
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Financeiro
                </Typography>
              }
            />
            <Grid
              container
              sx={{ mt: 2, p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}
            >
              <Grid item sx={{ width: '15%' }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Data a Receber
                </Typography>
              </Grid>
              <Grid item sx={{ width: '40%' }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Descrição
                </Typography>
              </Grid>
              <Grid item sx={{ width: '10%' }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Tipo de Recebimento
                </Typography>
              </Grid>
              <Grid item sx={{ width: '15%', textAlign: 'right' }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Valor
                </Typography>
              </Grid>
              <Grid item sx={{ width: '15%', textAlign: 'center' }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Status
                </Typography>
              </Grid>
              <Grid item sx={{ width: '5%' }}></Grid>
            </Grid>

            {incomes.length === 0 && (
              <Box sx={{ textAlign: 'center', mt: 4, p: 2 }}>
                <Typography>Nenhuma entrada encontrada para o paciente.</Typography>
              </Box>
            )}

            {incomes.map((income) => (
              <Grid
                container
                key={income.id}
                sx={{
                  p: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                  alignItems: 'center',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Grid item sx={{ width: '15%' }}>
                  <Typography variant="body2">{income.dueDate}</Typography>
                </Grid>

                <Grid item sx={{ width: '40%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">{income.description}</Typography>
                  </Box>
                </Grid>

                <Grid item sx={{ width: '10%' }}>
                  <Typography variant="body2">{income.receiptMethodType}</Typography>
                </Grid>

                <Grid item sx={{ width: '15%', textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {income.amount}
                  </Typography>
                </Grid>

                <Grid item sx={{ width: '15%', textAlign: 'center' }}>
                  <Chip
                    label={income.status}
                    color={statusConfig[income.status].color}
                    variant={statusConfig[income.status].variant}
                    size="small"
                    sx={{ fontWeight: 'bold', minWidth: '85px' }}
                  />
                </Grid>

                <Grid item sx={{ width: '5%', textAlign: 'center' }}>
                  <IconButton size="small" onClick={(e) => openMenu(e, income)}>
                    <MoreVertIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}

            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu}>
              <MenuItem onClick={editIncome}>Editar</MenuItem>
              <MenuItem onClick={receive} disabled={menuSelectedIncome?.status === 'Recebida'}>
                Receber
              </MenuItem>

              <MenuItem onClick={clickDelete} sx={{ color: 'error.main' }}>
                Excluir
              </MenuItem>
            </Menu>

            {incomes.length > 0 && (
              <Grid container sx={{ p: 1, mt: 1, justifyContent: 'flex-end' }}>
                <Grid item>
                  <Stack direction="column" alignItems="flex-end">
                    <Typography variant="caption" color="text.secondary">
                      Total: <strong>{totalAmount}</strong>
                    </Typography>

                    <Typography variant="caption" color="success.main" sx={{ mt: 0.5 }}>
                      Total Recebido: <strong>{totalAmountReceived}</strong>
                    </Typography>

                    <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
                      Total a Receber: <strong>{totalAmountToBeReceived}</strong>
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item sx={{ width: '5%' }}></Grid>
              </Grid>
            )}

            {openIncomeModal && (
              <IncomesModal
                open={openIncomeModal}
                onClose={() => setOpenIncomeModal(false)}
                onSaveSuccess={saveSuccess}
                incomeId={selectedIncomeId}
                incomeOrigin={'FINANCIAL'}
                patientId={patientId}
              />
            )}

            {openReceiptModal && (
              <ReceiptModal
                open={openReceiptModal}
                onClose={() => setOpenReceiptModal(false)}
                onSaveSuccess={saveSuccess}
                onSaveError={saveError}
                incomeId={selectedIncomeId}
              />
            )}

            <Dialog open={openDeleteModal} onClose={closeDeleteModal}>
              <DialogTitle>Confirmar Exclusão</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Tem certeza que deseja excluir essa entrada?
                  <br />
                  Essa ação não pode ser desfeita.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={closeDeleteModal} color="primary" variant="outlined">
                  Cancelar
                </Button>
                <Button onClick={confirmDelete} color="error" variant="outlined" autoFocus>
                  Excluir
                </Button>
              </DialogActions>
            </Dialog>
          </Card>
        </Box>
      </Box>
    </>
  );
}
